import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../app/hooks';
import { useEntitlements } from '../app/hooks/useEntitlements';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { RootState } from '../app/store';
import {
  fetchExpensesPaged,
  deleteExpense,
  upsertExpenseLocal,
  recomputeExpenseAggregates,
} from '../app/slices/expenseSlice';
import { addToast } from '../app/slices/uiSlice';
import LoadingSpinner from '../components/LoadingSpinner';
import ExpenseForm from '../components/ExpenseForm';
import InlinePanel from '../ui/common/InlinePanel';
import ReceiptPreview from '../ui/common/ReceiptPreview';
import {
  flag,
  idFromFlag,
  getPageFromUrl,
  setPageInUrl,
  focusRef as focusRefUtil,
} from '../lib/urlState';
import { trackUsage, startDwellTimer } from '../lib/telemetry';
import {
  PlusIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ReceiptRefundIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { ExpenseCategory, EXPENSE_TAGS } from '../types/models';
import { gstUtils } from '../lib/calculateGST';
import { track } from '../lib/firebase';
import { Link } from 'react-router-dom';

const Expenses: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const { expenses, isLoading } = useSelector((state: RootState) => state.expenses);
  const { canWrite, hasArchiveAccess } = useEntitlements();

  const addBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategory | ''>('');
  const [showFilters, setShowFilters] = useState(false);
  const [receiptPreviewState, setReceiptPreviewState] = useState<{
    isOpen: boolean;
    imageUrl: string;
  }>({
    isOpen: false,
    imageUrl: '',
  });

  // URL flags
  const isNewOpen = flag('new');
  const editId = idFromFlag('edit');
  const isEditOpen = Boolean(editId);

  const closePanel = () => {
    const p = new URLSearchParams(location.search);
    ['new', 'edit'].forEach(k => p.delete(k));
    const qs = p.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
  };

  const [page, setPage] = useState(getPageFromUrl());
  useEffect(() => {
    if (user?.uid) {
      dispatch(fetchExpensesPaged({ userId: user.uid, pageSize: 10 }));
    }
  }, [dispatch, user?.uid]);

  useEffect(() => {
    // Track page view and dwell time
    trackUsage('usage_page_view', { page: 'Expenses' }).catch(console.warn);
    const stop = startDwellTimer('Expenses');
    return stop;
  }, []);

  // Filter expenses based on search and filters
  const filteredExpenses = expenses.filter((expense, index) => {
    // Log first 3 expenses from Redux state
    if (index < 3) {
      console.log(`[Expenses] Expense #${index} from Redux state:`, {
        id: expense.id,
        category: expense.category,
        subCategory: expense.subCategory,
        amount: expense.amount,
        date: expense.date,
        dateType: typeof expense.date,
        allKeys: Object.keys(expense),
        fullExpense: JSON.stringify(expense, (key, value) => {
          if (value instanceof Date) return value.toISOString();
          if (value?.toMillis) return `Timestamp(${value.toMillis()})`;
          return value;
        }).substring(0, 300),
      });
    }

    // Skip invalid expenses (missing required fields)
    if (!expense.category || !expense.subCategory || !expense.date) {
      console.error('[Expenses] ❌ Skipping INVALID expense:', {
        id: expense.id,
        category: expense.category,
        subCategory: expense.subCategory,
        date: expense.date,
        amount: expense.amount,
        allKeys: Object.keys(expense),
        hasUndefinedFields: Object.entries(expense)
          .filter(([, v]) => v === undefined)
          .map(([k]) => k),
      });
      return false;
    }

    const matchesSearch =
      searchTerm === '' ||
      expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.subCategory?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = selectedCategory === '' || expense.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Debug logging for expense data integrity
  React.useEffect(() => {
    const expensesWithMissingData = expenses.filter(e => !e.category || !e.subCategory);
    if (expensesWithMissingData.length > 0) {
      console.error(
        '[Expenses] Found expenses with missing category/subCategory:',
        expensesWithMissingData.map(e => ({
          id: e.id,
          category: e.category,
          subCategory: e.subCategory,
          amount: e.amount,
          date: e.date,
        }))
      );
    }
  }, [expenses]);

  const handleDeleteExpense = async (expenseId: string) => {
    if (!user?.uid) return;

    if (
      window.confirm('Are you sure you want to delete this expense? This action cannot be undone.')
    ) {
      try {
        await dispatch(deleteExpense({ userId: user.uid, expenseId }));
        dispatch(
          addToast({
            message: 'Expense deleted successfully',
            type: 'success',
          })
        );
      } catch {
        dispatch(
          addToast({
            message: 'Failed to delete expense',
            type: 'error',
          })
        );
      }
    }
  };

  const handleEditExpense = (expenseId: string) => {
    trackUsage('usage_action', { page: 'Expenses', action: 'edit_expense', expenseId }).catch(
      console.warn
    );
    const p = new URLSearchParams(location.search);
    p.set('edit', expenseId);
    navigate({ pathname: location.pathname, search: `?${p.toString()}` }, { replace: false });
  };

  const handleViewReceipt = (receiptUrl: string) => {
    trackUsage('usage_action', { page: 'Expenses', action: 'receipt_view' }).catch(console.warn);
    setReceiptPreviewState({
      isOpen: true,
      imageUrl: receiptUrl,
    });
  };

  const getCategoryLabel = (category: ExpenseCategory): string => {
    const categoryMap: Record<any, string> = {
      fuel: 'Fuel / Gas',
      maintenance: 'Maintenance',
      'vehicle-upkeep': 'Vehicle Upkeep',
      insurance: 'Insurance',
      'phone-data': 'Phone & Data',
      accessories: 'Accessories',
      clothing: 'Clothing',
      meals: 'Meals / Snacks',
      entertainment: 'Entertainment',
      miscellaneous: 'Miscellaneous',
      'lease-payment': 'Lease Payment',
      'loan-interest': 'Loan Interest',
      'tax-services': 'Tax Services',
    };
    return categoryMap[category] || category;
  };

  const getGSTClaimableStatus = (category: ExpenseCategory, subCategory: string): boolean => {
    if (!category) {
      console.error('[Expenses] getGSTClaimableStatus called with undefined category!', {
        category,
        subCategory,
        stackTrace: new Error().stack,
      });
      return false;
    }

    const tags = EXPENSE_TAGS[category];
    if (!tags || !Array.isArray(tags)) {
      console.warn('[Expenses] No tags found for category:', category);
      return false;
    }
    const tag = tags.find(t => t.name === subCategory);
    return tag?.gstClaimable || false;
  };

  if (isLoading && !(isNewOpen || isEditOpen)) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading expenses..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* CRITICAL: Province Not Set Warning */}
      {(!user?.province || user.province === '') && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border-2 border-red-400 rounded-xl p-5 shadow-lg"
        >
          <div className="flex items-start space-x-3">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-red-900 mb-2">
                ⚠️ Action Required: Set Your Province
              </h3>
              <p className="text-sm text-red-800 mb-3">
                <strong>You cannot add expenses until you set your province.</strong> Tax rates vary
                by province!
              </p>
              <Link
                to="/profile"
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors shadow-md"
              >
                Go to Profile Settings →
              </Link>
            </div>
          </div>
        </motion.div>
      )}

      {/* Branding Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-5 shadow-md"
      >
        <div className="flex items-center justify-center space-x-3 mb-2">
          <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-lg">A</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
            AutoTaxPilot™
          </h1>
        </div>
        <p className="text-center text-sm text-gray-600 font-medium">
          Maximize Your Deductions • Best ITCs • Lowest Clicks
        </p>
      </motion.div>

      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-3xl font-bold leading-7 text-gray-900 sm:text-4xl sm:truncate"
          >
            Business Expenses
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-2 text-base text-gray-600"
          >
            Track and manage your deductible business expenses
          </motion.p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          {canWrite ? (
            <button
              ref={addBtnRef}
              onClick={() => {
                trackUsage('usage_action', { page: 'Expenses', action: 'add_expense' }).catch(
                  console.warn
                );
                const p = new URLSearchParams(location.search);
                p.set('new', '1');
                navigate(
                  { pathname: location.pathname, search: `?${p.toString()}` },
                  { replace: false }
                );
                track('panel_opened', { panel: 'expense_new' });
              }}
              className="btn-primary"
              data-ref="addExpenseBtn"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Expense
            </button>
          ) : hasArchiveAccess ? (
            <div className="text-sm text-gray-500 flex items-center">
              <svg className="h-4 w-4 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              Read-only access
            </div>
          ) : null}
        </div>
      </div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search expenses..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
          </div>

          {/* Filter Controls */}
          <div className="flex items-center space-x-4">
            <select
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value as ExpenseCategory | '')}
              className="input"
            >
              <option value="">All Categories</option>
              <option value="fuel">Fuel / Gas</option>
              <option value="maintenance">Maintenance</option>
              <option value="vehicle-upkeep">Vehicle Upkeep</option>
              <option value="insurance">Insurance</option>
              <option value="phone-data">Phone & Data</option>
              <option value="accessories">Accessories</option>
              <option value="clothing">Clothing</option>
              <option value="meals">Meals / Snacks</option>
              <option value="entertainment">Entertainment</option>
              <option value="miscellaneous">Miscellaneous</option>
            </select>

            <button onClick={() => setShowFilters(!showFilters)} className="btn-secondary">
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>
        </div>

        {/* Advanced Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 pt-4 border-t border-gray-200"
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="label">Date Range</label>
                  <select className="input">
                    <option>All Time</option>
                    <option>This Month</option>
                    <option>Last Month</option>
                    <option>This Quarter</option>
                    <option>This Year</option>
                  </select>
                </div>
                <div>
                  <label className="label">Amount Range</label>
                  <select className="input">
                    <option>Any Amount</option>
                    <option>Under $50</option>
                    <option>$50 - $200</option>
                    <option>$200 - $500</option>
                    <option>Over $500</option>
                  </select>
                </div>
                <div>
                  <label className="label">GST Status</label>
                  <select className="input">
                    <option>All</option>
                    <option>GST Claimable</option>
                    <option>No GST</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* 1) PANEL */}
      <InlinePanel
        open={isNewOpen || isEditOpen}
        title={isEditOpen ? 'Edit Expense' : 'Add New Expense'}
        subtitle="Receipts are preferred for audits."
        onClose={closePanel}
        className="max-w-2xl mx-auto"
        returnFocusRef={addBtnRef as unknown as React.RefObject<HTMLElement>}
        regionId="expense-inline-panel-title"
      >
        <ExpenseForm
          mode={isEditOpen ? 'edit' : 'create'}
          expenseId={editId}
          onCancel={closePanel}
          onSaved={(saved: any) => {
            dispatch(upsertExpenseLocal(saved));
            dispatch(recomputeExpenseAggregates());
            closePanel();
            focusRefUtil('addExpenseBtn');
          }}
        />
      </InlinePanel>

      {/* 2) SUMMARY */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card"
      >
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{filteredExpenses.length}</p>
            <p className="text-sm text-gray-500">Total Expenses</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {gstUtils.formatCurrency(
                filteredExpenses.reduce((sum, expense) => sum + (expense.amount || 0), 0)
              )}
            </p>
            <p className="text-sm text-gray-500">Total Amount</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {gstUtils.formatCurrency(
                filteredExpenses.reduce((sum, expense) => sum + (expense.claimableITC || 0), 0)
              )}
            </p>
            <p className="text-sm text-gray-500">Total ITCs</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {gstUtils.formatCurrency(
                filteredExpenses.reduce(
                  (sum, expense) =>
                    sum + ((expense.amount || 0) * (expense.businessUsePct || 0)) / 100,
                  0
                )
              )}
            </p>
            <p className="text-sm text-gray-500">Deductible Amount</p>
          </div>
        </div>
      </motion.div>

      {/* 3) LIST */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card"
      >
        {filteredExpenses.length === 0 ? (
          <div className="text-center py-12">
            <ReceiptRefundIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No expenses found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {expenses.length === 0
                ? 'Start tracking your business expenses to maximize deductions.'
                : 'Try adjusting your search or filters.'}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    GST/ITC
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Business Use
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredExpenses.map((expense, index) => (
                  <motion.tr
                    key={expense.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {expense.description}
                        </div>
                        <div className="text-sm text-gray-500">
                          {expense.date instanceof Date && !isNaN(expense.date.getTime())
                            ? expense.date.toLocaleDateString('en-CA')
                            : new Date(expense.date).toLocaleDateString('en-CA')}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900">{expense.subCategory}</div>
                        <div className="text-sm text-gray-500">
                          {getCategoryLabel(expense.category)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {gstUtils.formatCurrency(expense.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900">
                          GST: {gstUtils.formatCurrency(expense.gstPaid)}
                        </div>
                        <div className="text-sm text-gray-500">
                          ITC: {gstUtils.formatCurrency(expense.claimableITC)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm text-gray-900">{expense.businessUsePct}%</span>
                        {getGSTClaimableStatus(expense.category, expense.subCategory) ? (
                          <span className="ml-2 badge-success">Claimable</span>
                        ) : (
                          <span className="ml-2 badge-gray">No GST</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {expense.receiptPhotoURL && (
                          <button
                            onClick={() => handleViewReceipt(expense.receiptPhotoURL!)}
                            className="text-primary-600 hover:text-primary-900"
                            title="View Receipt"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEditExpense(expense.id)}
                          className="text-gray-600 hover:text-gray-900"
                          title="Edit Expense"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteExpense(expense.id)}
                          className="text-danger-600 hover:text-danger-900"
                          title="Delete Expense"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
            {/* Paging controls */}
            <div className="flex items-center justify-between px-6 py-3 border-t mt-4">
              <div className="text-xs text-gray-500">Page {page}</div>
              <div className="flex gap-2">
                <button
                  className="btn-secondary btn-sm"
                  disabled={page === 1}
                  onClick={() => {
                    if (!user?.uid) return;
                    setPage(1);
                    dispatch(fetchExpensesPaged({ userId: user.uid, pageSize: 10 }));
                    setPageInUrl(1);
                  }}
                >
                  Prev
                </button>
                <button
                  className="btn-secondary btn-sm"
                  onClick={() => {
                    if (!user?.uid || filteredExpenses.length === 0) return;
                    const last = filteredExpenses[filteredExpenses.length - 1];
                    const cursor = {
                      dateMs: new Date(last.date).getTime(),
                      uploadedAtMs:
                        last.uploadedAt instanceof Date
                          ? last.uploadedAt.getTime()
                          : (last.uploadedAt as any).toMillis(),
                    };
                    dispatch(fetchExpensesPaged({ userId: user.uid, pageSize: 10, after: cursor }));
                    const nextPage = page + 1;
                    setPage(nextPage);
                    setPageInUrl(nextPage);
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Receipt Preview Modal */}
      <ReceiptPreview
        isOpen={receiptPreviewState.isOpen}
        onClose={() => setReceiptPreviewState({ isOpen: false, imageUrl: '' })}
        imageUrl={receiptPreviewState.imageUrl}
      />
    </div>
  );
};

export default Expenses;
